import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, ConfirmationModal } from '../components';
import { Button } from '../components/design-system';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';

// ─── Unit Card ────────────────────────────────────────────────────────────────

const UnitCard: React.FC<{
  unit: any;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ unit, onEdit, onDelete }) => {
  const styles = useThemedStyles(getStyles);
  return (
  <View style={styles.card}>
    <View style={styles.cardIcon}>
      <Ionicons name="scale-outline" size={22} color={colors.primary} />
    </View>
    <View style={styles.cardBody}>
      <Text style={styles.cardName} numberOfLines={1}>{unit.name}</Text>
      {unit.abbreviation ? (
        <Text style={styles.cardSub}>{unit.abbreviation}</Text>
      ) : null}
      {unit.description ? (
        <Text style={styles.cardDesc} numberOfLines={2}>{unit.description}</Text>
      ) : null}
    </View>
    <View style={styles.cardActions}>
      {!unit.isDefault && (
        <>
          <TouchableOpacity onPress={onEdit} style={styles.actionBtn} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="pencil-outline" size={18} color={colors.textLight} />
          </TouchableOpacity>
          <TouchableOpacity onPress={onDelete} style={styles.actionBtn} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="trash-outline" size={18} color={colors.error} />
          </TouchableOpacity>
        </>
      )}
      {unit.isDefault && (
        <View style={styles.defaultBadge}>
          <Text style={styles.defaultBadgeText}>Default</Text>
        </View>
      )}
    </View>
  </View>
  );
};

// ─── Inline form (web) ────────────────────────────────────────────────────────

interface UnitFormFields {
  name: string;
  abbreviation: string;
  description: string;
}

interface InlineFormProps {
  fields: UnitFormFields;
  onChange: (fields: UnitFormFields) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  editingUnit: any;
}

const InlineUnitForm: React.FC<InlineFormProps> = ({ fields, onChange, onSave, onCancel, saving, editingUnit }) => {
  const inlineStyles = useThemedStyles(getInlineStyles);
  return (
  <View style={inlineStyles.card}>
    <View style={inlineStyles.cardHeader}>
      <Text style={inlineStyles.cardTitle}>{editingUnit ? 'Edit Unit' : 'New Unit'}</Text>
      <TouchableOpacity onPress={onCancel} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
        <Ionicons name="close" size={20} color={colors.textLight} />
      </TouchableOpacity>
    </View>

    <View style={inlineStyles.row}>
      <View style={inlineStyles.fieldMajor}>
        <Text style={inlineStyles.label}>Name <Text style={inlineStyles.required}>*</Text></Text>
        <TextInput
          style={inlineStyles.input}
          value={fields.name}
          onChangeText={(t) => onChange({ ...fields, name: t })}
          placeholder="e.g. Box, Liter, Session, Consultant Hour"
          placeholderTextColor={colors.textLight}
          autoFocus
        />
      </View>
      <View style={inlineStyles.fieldMinor}>
        <Text style={inlineStyles.label}>Abbreviation</Text>
        <TextInput
          style={inlineStyles.input}
          value={fields.abbreviation}
          onChangeText={(t) => onChange({ ...fields, abbreviation: t })}
          placeholder="e.g. box, L, hr"
          placeholderTextColor={colors.textLight}
          autoCapitalize="none"
        />
      </View>
    </View>

    <Text style={inlineStyles.label}>Description</Text>
    <TextInput
      style={[inlineStyles.input, inlineStyles.textarea]}
      value={fields.description}
      onChangeText={(t) => onChange({ ...fields, description: t })}
      placeholder="Optional clarification for your team"
      placeholderTextColor={colors.textLight}
      multiline
      numberOfLines={2}
      textAlignVertical="top"
    />

    <View style={inlineStyles.actions}>
      <Button title="Cancel" variant="secondary" onPress={onCancel} style={inlineStyles.btn} />
      <Button title={editingUnit ? 'Update' : 'Create'} variant="primary" onPress={onSave} loading={saving} style={inlineStyles.btn} />
    </View>
  </View>
  );
};

const getInlineStyles = () => StyleSheet.create({
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.lg,
    marginBottom: spacing.md,
    borderWidth: 1.5,
    borderColor: colors.primary + '30',
    ...designTokens.shadows.sm,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  cardTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  row: {
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.xs,
  },
  fieldMajor: { flex: 2 },
  fieldMinor: { flex: 1 },
  label: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    marginBottom: spacing.xs,
    marginTop: spacing.xs,
  },
  required: { color: colors.error },
  input: {
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
    backgroundColor: colors.background.surface,
  },
  textarea: {
    minHeight: 64,
    paddingTop: spacing.sm,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.md,
    justifyContent: 'flex-end',
  },
  btn: { minWidth: 100 },
});

// ─── Mobile bottom-sheet modal ────────────────────────────────────────────────

interface MobileModalProps {
  visible: boolean;
  fields: UnitFormFields;
  onChange: (fields: UnitFormFields) => void;
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
  editingUnit: any;
}

const MobileUnitModal: React.FC<MobileModalProps> = ({ visible, fields, onChange, onSave, onClose, saving, editingUnit }) => {
  const modalStyles = useThemedStyles(getModalStyles);
  return (
  <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
    <View style={modalStyles.overlay}>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ width: '100%' }}>
        <View style={modalStyles.sheet}>
          <View style={modalStyles.handle} />

          <View style={modalStyles.header}>
            <Text style={modalStyles.title}>{editingUnit ? 'Edit Unit' : 'New Unit'}</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close" size={24} color={colors.textDark} />
            </TouchableOpacity>
          </View>

          <ScrollView style={modalStyles.body} keyboardShouldPersistTaps="handled">
            <Text style={modalStyles.label}>Name <Text style={modalStyles.required}>*</Text></Text>
            <TextInput
              style={modalStyles.input}
              value={fields.name}
              onChangeText={(t) => onChange({ ...fields, name: t })}
              placeholder="e.g. Box, Liter, Session, Consultant Hour"
              placeholderTextColor={colors.textLight}
              autoFocus
            />

            <Text style={modalStyles.label}>Abbreviation</Text>
            <TextInput
              style={modalStyles.input}
              value={fields.abbreviation}
              onChangeText={(t) => onChange({ ...fields, abbreviation: t })}
              placeholder="e.g. box, L, sess, hr"
              placeholderTextColor={colors.textLight}
              autoCapitalize="none"
            />

            <Text style={modalStyles.label}>Description</Text>
            <TextInput
              style={[modalStyles.input, modalStyles.textarea]}
              value={fields.description}
              onChangeText={(t) => onChange({ ...fields, description: t })}
              placeholder="Optional clarification for your team"
              placeholderTextColor={colors.textLight}
              multiline
              numberOfLines={3}
              textAlignVertical="top"
            />
          </ScrollView>

          <View style={modalStyles.footer}>
            <Button title="Cancel" variant="secondary" onPress={onClose} style={modalStyles.footerBtn} />
            <Button title={editingUnit ? 'Update' : 'Create'} variant="primary" onPress={onSave} loading={saving} style={modalStyles.footerBtn} />
          </View>
        </View>
      </KeyboardAvoidingView>
    </View>
  </Modal>
  );
};

// ─── Screen ───────────────────────────────────────────────────────────────────

export const UnitsListScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();

  const [units, setUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Form state — shared between inline (web) and modal (mobile)
  const [showForm, setShowForm] = useState(false);
  const [editingUnit, setEditingUnit] = useState<any>(null);
  const [formFields, setFormFields] = useState<UnitFormFields>({ name: '', abbreviation: '', description: '' });
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const isWeb = Platform.OS === 'web';

  const load = useCallback(async (reset = false) => {
    try {
      setError(null);
      if (reset) setLoading(true);
      const res = await apiService.getUnits({ limit: 200 });
      if (res.statusCode === 1000 && res.data) {
        setUnits(res.data.units || []);
        setTotal(res.data.pagination?.total || res.data.units?.length || 0);
      } else {
        setError(res.errors?.[0]?.message || 'Failed to load units');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load units');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(true); }, [load]);

  const handleRefresh = () => { setRefreshing(true); load(false); };

  const openCreate = () => {
    setEditingUnit(null);
    setFormFields({ name: '', abbreviation: '', description: '' });
    setShowForm(true);
  };

  const openEdit = (unit: any) => {
    setEditingUnit(unit);
    setFormFields({ name: unit.name || '', abbreviation: unit.abbreviation || '', description: unit.description || '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formFields.name.trim()) {
      showNotification('error', 'Validation', 'Unit name is required');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: formFields.name.trim(),
        abbreviation: formFields.abbreviation.trim() || undefined,
        description: formFields.description.trim() || undefined,
      };
      const res = editingUnit
        ? await apiService.updateUnit(editingUnit._id, payload)
        : await apiService.createUnit(payload);

      if (res.statusCode === 1000) {
        showNotification('success', 'Saved', editingUnit ? 'Unit updated' : 'Unit created');
        setShowForm(false);
        setEditingUnit(null);
        load(false);
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to save unit');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message || 'Failed to save unit');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseForm = () => { setShowForm(false); setEditingUnit(null); };

  const confirmDelete = async () => {
    if (!unitToDelete) return;
    try {
      setDeleting(true);
      const res = await apiService.deleteUnit(unitToDelete);
      if (res.statusCode === 1000) {
        showNotification('success', 'Deleted', 'Unit removed');
        setShowDeleteModal(false);
        setUnitToDelete(null);
        load(false);
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to delete');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message);
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const renderListContent = () => {
    if (loading && units.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>Loading units...</Text>
        </View>
      );
    }

    if (error && units.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
          <Text style={styles.errorTitle}>Unable to Load Units</Text>
          <Text style={styles.errorText}>{error}</Text>
          <Button title="Try Again" onPress={() => load(true)} variant="primary" style={styles.retryButton} />
        </View>
      );
    }

    if (units.length === 0 && !showForm) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="scale-outline" size={64} color={colors.border} />
          <Text style={styles.errorTitle}>No units defined</Text>
          <Text style={styles.errorText}>
            Define measurement units for your products and services — items, boxes, sessions, hours, and more.
          </Text>
          <Button title="Add Unit" onPress={openCreate} variant="primary" style={styles.retryButton} />
        </View>
      );
    }

    return (
      <FlatList
        data={units}
        keyExtractor={(item) => item._id}
        ListHeaderComponent={
          // Web: inline form at top of list
          isWeb && showForm ? (
            <InlineUnitForm
              fields={formFields}
              onChange={setFormFields}
              onSave={handleSave}
              onCancel={handleCloseForm}
              saving={saving}
              editingUnit={editingUnit}
            />
          ) : null
        }
        renderItem={({ item }) => (
          <UnitCard
            unit={item}
            onEdit={() => openEdit(item)}
            onDelete={() => { setUnitToDelete(item._id); setShowDeleteModal(true); }}
          />
        )}
        contentContainerStyle={styles.listContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.primary} />}
        ItemSeparatorComponent={() => <View style={styles.separator} />}
      />
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
    >
      <View style={styles.container}>
        {/* Header bar */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              <Text style={styles.headerTitle}>Units</Text>
              {!loading && (
                <Text style={styles.headerSubtitle}>
                  {total} {total === 1 ? 'unit' : 'units'} defined
                </Text>
              )}
            </View>
            <View style={styles.headerActions}>
              <TouchableOpacity
                style={styles.addButton}
                onPress={openCreate}
                activeOpacity={0.8}
              >
                <Ionicons name="add" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>
          </View>
          <Text style={styles.headerHint}>
            Units let you specify how products and services are measured — per box, per session, per hour, and more.
          </Text>
        </View>

        {/* List */}
        <View style={styles.listContainer}>
          {renderListContent()}
        </View>
      </View>

      {/* Mobile: bottom-sheet modal */}
      {!isWeb && (
        <MobileUnitModal
          visible={showForm}
          fields={formFields}
          onChange={setFormFields}
          onSave={handleSave}
          onClose={handleCloseForm}
          saving={saving}
          editingUnit={editingUnit}
        />
      )}

      <ConfirmationModal
        visible={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setUnitToDelete(null); }}
        onConfirm={confirmDelete}
        title="Remove Unit"
        message="Are you sure you want to remove this unit? Products using it will keep their current value."
        confirmText="Remove"
        cancelText="Cancel"
        type="error"
        loading={deleting}
      />
    </ScreenLayout>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  header: {
    backgroundColor: colors.background.surface,
    paddingTop: spacing.lg,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    ...designTokens.shadows.sm,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  headerLeft: { flex: 1 },
  headerTitle: {
    fontSize: typography.fontSize.xxl,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
    marginBottom: spacing.xs,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  headerHint: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: spacing.xs,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  addButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    ...designTokens.shadows.sm,
  },
  listContainer: { flex: 1 },
  listContent: {
    padding: spacing.md,
    paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg,
  },
  separator: { height: spacing.sm },
  // ─── Card ─────────────────────────────────────────────────────────────────
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.md,
    gap: spacing.md,
    ...designTokens.shadows.sm,
  },
  cardIcon: {
    width: 44,
    height: 44,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.primary + '14',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardBody: { flex: 1 },
  cardName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  cardSub: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: 2,
  },
  cardDesc: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: 2,
  },
  cardActions: { flexDirection: 'row', gap: spacing.xs, alignItems: 'center' },
  actionBtn: { padding: spacing.xs },
  defaultBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: 10,
    backgroundColor: colors.primary + '18',
  },
  defaultBadgeText: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  // ─── States ───────────────────────────────────────────────────────────────
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxl,
  },
  centerText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  errorTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
    fontFamily: typography.fontFamily.semibold,
    textAlign: 'center',
  },
  errorText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textAlign: 'center',
    marginBottom: spacing.xl,
    fontFamily: typography.fontFamily.regular,
  },
  retryButton: { minWidth: 160 },
});

// ─── Mobile Modal Styles ──────────────────────────────────────────────────────

const getModalStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '85%',
  },
  handle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.border,
    alignSelf: 'center',
    marginTop: spacing.sm,
    marginBottom: spacing.xs,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  body: { padding: spacing.lg },
  label: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    marginBottom: spacing.xs,
    marginTop: spacing.sm,
  },
  required: { color: colors.error },
  input: {
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
    backgroundColor: colors.background.surface,
  },
  textarea: {
    minHeight: 80,
    paddingTop: spacing.sm,
  },
  footer: {
    flexDirection: 'row',
    gap: spacing.sm,
    padding: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  footerBtn: { flex: 1 },
});
